// Receipts and screenshots attached to a payment.
//
// Everything in here is pure, so the rules the server enforces can be checked in
// the browser BEFORE a byte is uploaded — a 10 MB file rejected after a slow
// upload is the worst possible way to learn about the limit. The numbers below
// mirror the API exactly; if the server ever changes them, change them here too.
//
// The other job of this file is the key shape. A payment carries `attachments`,
// but the per-bill GET /bills/{bill_id}/payments Title-Cases every top-level key
// on the way out, so the same list can arrive as `Attachments` (the aggregate
// GET /bills/payments does not Title-Case). readPaymentAttachments() is the one
// place that knows both spellings.

export const MAX_FILES_PER_UPLOAD = 5;
export const MAX_FILES_PER_PAYMENT = 10;
export const MAX_FILE_BYTES = 10 * 1024 * 1024;

export const ALLOWED_ATTACHMENT_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/heic',
  'image/heif',
  'application/pdf',
];

// Safari and some Android browsers hand over an empty `file.type` for HEIC/HEIF
// (and occasionally for WEBP), so the extension is the fallback answer to "what
// is this?" rather than a second, looser rule.
const EXTENSION_TYPES = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
  pdf: 'application/pdf',
};

// What goes in the file input's `accept`. Extensions are included because the
// MIME types alone leave HEIC files greyed out in some file pickers.
export const ATTACHMENT_ACCEPT = [
  ...ALLOWED_ATTACHMENT_TYPES,
  ...Object.keys(EXTENSION_TYPES).map((ext) => `.${ext}`),
].join(',');

export const ALLOWED_TYPES_LABEL = 'PNG, JPEG, WEBP, HEIC, HEIF or PDF';

const extensionOf = (name = '') => {
  const parts = String(name).split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

// The type we will treat the file as: what the browser said, or what the file is
// named when the browser said nothing.
export const resolveFileType = (file) => {
  const declared = (file?.type || '').toLowerCase();
  if (declared) return declared;
  return EXTENSION_TYPES[extensionOf(file?.name)] || '';
};

export const formatFileSize = (bytes) => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size < 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

// A payment without the field has no attachments — not an error, just the normal
// case for every payment recorded before this existed.
export const readPaymentAttachments = (payment) => {
  const list = payment?.attachments ?? payment?.Attachments;
  return Array.isArray(list) ? list : [];
};

export const attachmentCount = (payment) => readPaymentAttachments(payment).length;

// Attachments have their own upload/delete endpoints, so the field is never
// edited as part of a payment. Both spellings are listed because the same list
// has to be kept out of the dynamically-derived column list (where it rendered
// as "[object Object]" in an editable input) and stripped from an edit payload
// (where PUTting the whole array back risked blanking it).
export const ATTACHMENT_FIELD_KEYS = ['attachments', 'Attachments'];

export const isAttachmentField = (field) => ATTACHMENT_FIELD_KEYS.includes(field);

export const isImageAttachment = (attachment) =>
  String(attachment?.content_type || '').toLowerCase().startsWith('image/');

// Checks one selection of files against every server rule at once and answers
// { accepted, errors }. `existingCount` is how many the payment already holds.
//
// A count violation (too many at once, or too many for the payment) rejects the
// whole selection — picking 8 files and silently uploading the first 5 would be
// worse than saying so. A per-file violation only drops that file, and the rest
// still upload; every rejection names the file and the reason.
export const validateAttachmentSelection = (files, existingCount = 0) => {
  const list = Array.from(files || []);
  const already = Number(existingCount) || 0;
  const errors = [];

  if (list.length === 0) {
    return { accepted: [], errors };
  }

  if (list.length > MAX_FILES_PER_UPLOAD) {
    errors.push(
      `Only ${MAX_FILES_PER_UPLOAD} files can be uploaded at a time — you picked ${list.length}.`
    );
  }

  const remaining = MAX_FILES_PER_PAYMENT - already;
  if (list.length > remaining) {
    errors.push(
      remaining <= 0
        ? `This payment already has the maximum of ${MAX_FILES_PER_PAYMENT} attachments. Remove one before adding another.`
        : `This payment already has ${already} of ${MAX_FILES_PER_PAYMENT} attachments, so you can add ${remaining} more — you picked ${list.length}.`
    );
  }

  // Count rules failed: nothing from this selection is uploaded.
  if (errors.length > 0) {
    return { accepted: [], errors };
  }

  const accepted = [];
  list.forEach((file) => {
    const name = file?.name || 'This file';
    const type = resolveFileType(file);

    if (!ALLOWED_ATTACHMENT_TYPES.includes(type)) {
      errors.push(`${name} is not a supported file type. Use ${ALLOWED_TYPES_LABEL}.`);
      return;
    }
    if (!(file.size > 0)) {
      errors.push(`${name} is empty.`);
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      errors.push(
        `${name} is ${formatFileSize(file.size)} — the limit is ${formatFileSize(MAX_FILE_BYTES)} per file.`
      );
      return;
    }
    accepted.push(file);
  });

  return { accepted, errors };
};
